import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export default function MissionPage() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const sold = stats?.sold ?? 0;
  const total = stats?.total ?? 500000;

  const photos = [
    { src: "/mission/stamperia-torchio.jpg", cap: t("missionPage.capTorchio"), id: "torchio" },
    { src: "/mission/stamperia-tipi.jpg", cap: t("missionPage.capTipi"), id: "tipi" },
    { src: "/mission/stamperia-fogli.jpg", cap: t("missionPage.capFogli"), id: "fogli" },
  ];

  return (
    <main className="min-h-screen bg-[#FAF8F5] pt-[57px]" data-testid="mission-page">
      {/* HERO */}
      <section className="px-5 md:px-12 pt-20 pb-16 border-b border-border/60">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-[10px] tracking-[0.4em] text-golddark mb-6"
        >
          {t("missionPage.eyebrow").toUpperCase()}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="font-display font-black tracking-tighter leading-[0.9] text-[13vw] md:text-[7.5vw]"
          data-testid="mission-page-title"
        >
          {t("missionPage.title")}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
          className="mt-8 text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl"
        >
          {t("missionPage.lead")}
        </motion.p>
      </section>

      {/* AI.BI. */}
      <section className="px-5 md:px-12 py-20 md:py-28 border-b border-border/60" data-testid="mission-aibi">
        <div className="grid md:grid-cols-[220px_1fr] gap-8 md:gap-16 items-start">
          <Reveal>
            <p className="text-[10px] tracking-[0.4em] text-golddark">{t("missionPage.aibiEyebrow")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black tracking-tighter leading-[1] text-3xl sm:text-4xl lg:text-5xl">
              {t("missionPage.aibiTitle")}
            </h2>
            <p className="mt-6 text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl">
              {t("missionPage.aibiBody")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* STAMPERIA ANTICA */}
      <section className="px-5 md:px-12 py-20 md:py-28 border-b border-border/60" data-testid="mission-stamperia">
        <div className="grid md:grid-cols-[220px_1fr] gap-8 md:gap-16 items-start">
          <Reveal>
            <p className="text-[10px] tracking-[0.4em] text-golddark">{t("missionPage.stampEyebrow")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black tracking-tighter leading-[1] text-3xl sm:text-4xl lg:text-5xl">
              {t("missionPage.stampTitle")}
            </h2>
            <p className="mt-6 text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl">
              {t("missionPage.stampBody")}
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="mission-photos">
          {photos.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <figure className="border border-border/60 p-2 bg-[#FFFFFF]">
                <img
                  src={p.src}
                  alt={p.cap}
                  loading="lazy"
                  className="w-full aspect-[3/4] object-cover"
                  data-testid={`mission-photo-${p.id}`}
                />
                <figcaption className="mt-3 mb-1 px-1 text-[9px] tracking-[0.2em] text-muted-foreground leading-relaxed">
                  {p.cap.toUpperCase()}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONTATORE */}
      <section className="px-5 md:px-12 py-20 md:py-28" data-testid="mission-counter-section">
        <Reveal>
          <p className="text-[10px] tracking-[0.4em] text-golddark mb-8">{t("missionPage.counterEyebrow")}</p>
          <div className="flex flex-wrap items-end gap-8 md:gap-14">
            <div className="font-mono text-6xl md:text-8xl font-light tabular-nums text-[#3C3735]" data-testid="mission-counter-value">
              <CountUp end={sold} duration={2.2} separator="." preserveValue />
            </div>
            <div className="pb-3 max-w-md">
              <p className="text-[10px] tracking-[0.3em] text-muted-foreground leading-relaxed">
                {t("missionPage.counterLabel").toUpperCase()}
              </p>
              <div className="mt-3 w-64 h-px bg-border relative overflow-hidden">
                <motion.div
                  className="absolute left-0 top-0 h-full bg-[#7D8C77]"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${Math.max((sold / total) * 100, 0.5)}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, ease: EASE }}
                />
              </div>
              <p className="mt-2 text-[10px] tracking-[0.3em] text-golddark">{t("missionPage.counterGoal").toUpperCase()}</p>
            </div>
          </div>
          <div className="mt-14">
            <Link
              to="/grid"
              data-testid="mission-cta"
              className="inline-block px-10 py-5 bg-[#7D8C77] text-[#FAF8F5] text-xs tracking-[0.25em] font-semibold hover:bg-[#3C3735] transition-colors duration-200"
            >
              {t("hero.ctaBuy").toUpperCase()}
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
